import { useState, useRef, useEffect } from 'react';
import {
  Button,
  Input,
  Spinner,
} from '@fluentui/react-components';
import {
  ChatMultiple24Regular,
  Dismiss24Regular,
  Send24Regular,
  Delete24Regular,
} from '@fluentui/react-icons';
import { useChatStore } from '@/store/chatStore';
import { openAIService } from '@/services/openai';
import { RichContentRenderer } from './RichContentRenderer';
import { useProjectStore } from '@/stores/projectStore';
import { mockTenants } from '@/services/mockData';

const PREDEFINED_QUESTIONS = [
  {
    id: 'q1',
    question: 'What is the Levered IRR for this project?',
    icon: '📊',
  },
  {
    id: 'q2',
    question: 'Show me a chart of project costs breakdown by category',
    icon: '💰',
  },
  {
    id: 'q3',
    question: 'What are the key assumptions in this model?',
    icon: '🔍',
  },
  {
    id: 'q4',
    question: 'Visualize the tenant mix and their annual revenue contribution',
    icon: '🏬',
  },
];

export function ChatBubble() {
  const { isOpen, messages, isLoading, toggleChat, addMessage, clearMessages, setLoading } =
    useChatStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { currentCalculations } = useProjectStore();

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePredefinedQuestion = async (question: string) => {
    await handleSendMessage(question);
  };

  // Detect if user is requesting a chart/visualization
  const isChartRequest = (text: string): boolean => {
    const chartKeywords = [
      'chart',
      'graph',
      'visualize',
      'visualization',
      'plot',
      'show breakdown',
      'breakdown',
      'compare',
      'comparison',
      'distribution',
      'trend',
      'show me a',
      'display',
    ];
    const lowerText = text.toLowerCase();
    return chartKeywords.some((keyword) => lowerText.includes(keyword));
  };

  // Extract JSON from OpenAI response (might be wrapped in code blocks)
  const extractJSON = (content: string): any | null => {
    // Clean the content first - remove numeric underscores which aren't valid JSON
    const cleanContent = (str: string) => {
      // Replace numeric underscores: 1_000_000 -> 1000000
      return str.replace(/(\d)_(\d)/g, '$1$2');
    };

    try {
      // Try to parse directly
      const cleaned = cleanContent(content);
      return JSON.parse(cleaned);
    } catch {
      // Try to extract from code blocks (```json or just ```)
      const jsonMatch = content.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
      if (jsonMatch) {
        try {
          const cleaned = cleanContent(jsonMatch[1]);
          return JSON.parse(cleaned);
        } catch (e) {
          console.error('Failed to parse JSON from code block:', e);
          return null;
        }
      }

      // Try to find JSON object without code blocks
      const objectMatch = content.match(/\{[\s\S]*\}/);
      if (objectMatch) {
        try {
          const cleaned = cleanContent(objectMatch[0]);
          return JSON.parse(cleaned);
        } catch (e) {
          console.error('Failed to parse JSON object:', e);
          return null;
        }
      }

      return null;
    }
  };

  // Validate chart data structure
  const isValidChartData = (data: any): boolean => {
    return (
      data &&
      typeof data === 'object' &&
      data.chartType &&
      ['bar', 'line', 'pie'].includes(data.chartType) &&
      Array.isArray(data.data) &&
      data.data.length > 0
    );
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    // Add user message
    addMessage({
      role: 'user',
      content: textToSend,
    });

    setInput('');
    setLoading(true);

    // Check if API key is configured
    if (!openAIService.hasApiKey()) {
      addMessage({
        role: 'assistant',
        content: 'Please configure your OpenAI API key in Settings to use the AI assistant.',
        type: 'error',
      });
      setLoading(false);
      return;
    }

    // Prepare context about the project
    const projectContext = currentCalculations
      ? `
Current Project Data:
- Levered IRR: ${(currentCalculations.returnMetrics.leveredIRR * 100).toFixed(2)}%
- Total Project Cost: $${(currentCalculations.totalProjectCost / 1000000).toFixed(2)}M
- Stabilized NOI: $${(currentCalculations.stabilizedNOI / 1000000).toFixed(2)}M
- Yield on Cost: ${(currentCalculations.yieldOnCost * 100).toFixed(2)}%
- Equity Multiple: ${currentCalculations.returnMetrics.equityMultiple.toFixed(2)}x
- Exit Cap Rate: ${(currentCalculations.noiCapAtStabilization * 100).toFixed(2)}%
`
      : 'No project data loaded.';

    // Prepare tenant context
    const tenantContext = `
Tenant Mix (${mockTenants.length} tenants):
${mockTenants
  .map(
    (t) =>
      `- ${t.name} (${t.category}): ${t.nra.toLocaleString()} SF @ $${t.rentPerSF}/SF, Annual Rent: $${((t.nra * t.rentPerSF) / 1000).toFixed(0)}K`
  )
  .join('\n')}
`;

    // Check if this is a chart request
    const needsChart = isChartRequest(textToSend);

    // Call OpenAI API
    const systemPrompt = needsChart
      ? `You are a helpful real estate development analysis assistant. You help users understand their real estate development model.

${projectContext}
${tenantContext}

The user is asking for a visualization. You MUST return ONLY valid JSON (no code blocks, no explanatory text before or after).

CRITICAL JSON FORMATTING RULES:
- Numbers must NOT use underscores (use 1326000, NOT 1_326_000)
- Do NOT wrap in code blocks or backticks
- Do NOT add any text before or after the JSON
- Return ONLY the raw JSON object starting with { and ending with }

Required JSON structure:
{
  "chartType": "bar",
  "title": "Chart title",
  "data": [
    {"name": "Category 1", "value": 100},
    {"name": "Category 2", "value": 150}
  ],
  "series": [
    {"key": "value", "name": "Series Name"}
  ],
  "description": "Brief description"
}

Guidelines:
- chartType: Use "bar" for comparisons, "line" for trends, "pie" for proportions
- Include all relevant data points from the project
- For tenant analysis, include all tenants with their revenue metrics
- For cost breakdown, use categories: Land, Construction, Soft Costs, Financing
- All numbers must be plain integers (1326000 not 1_326_000)
- Keep descriptions concise (1-2 sentences)`
      : `You are a helpful real estate development analysis assistant. You help users understand their real estate development model.

${projectContext}
${tenantContext}

When answering questions:
- Be concise and specific
- Use markdown formatting for clarity
- Focus on actionable insights
- Use bullet points for lists
- Format numbers clearly (use $ for money, % for percentages)`;

    const response = await openAIService.chatCompletion([
      {
        role: 'system',
        content: systemPrompt,
      },
      ...messages.map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      })),
      {
        role: 'user',
        content: textToSend,
      },
    ]);

    setLoading(false);

    if (response.error) {
      addMessage({
        role: 'assistant',
        content: response.error,
        type: 'error',
      });
    } else {
      // Try to extract JSON if it's a chart request
      if (needsChart) {
        const chartData = extractJSON(response.content);
        if (chartData && isValidChartData(chartData)) {
          // Successfully parsed and validated - render the chart
          addMessage({
            role: 'assistant',
            content: chartData.description || chartData.title || 'Here is the visualization:',
            type: 'chart',
            data: chartData,
          });
        } else {
          // JSON parsing failed - NEVER show raw JSON to user
          // Instead show a friendly error and suggest rephrasing
          console.error('Failed to parse chart data from response:', response.content);
          addMessage({
            role: 'assistant',
            content:
              "I'm having trouble creating that visualization. Could you try rephrasing your question? For example:\n\n- 'Show me a bar chart of tenant revenue'\n- 'Visualize project costs breakdown'\n- 'Display top tenants by square footage'",
            type: 'error',
          });
        }
      } else {
        addMessage({
          role: 'assistant',
          content: response.content,
        });
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 w-14 h-14 bg-salesforce-blue hover:bg-electric-blue text-white rounded-full shadow-4 flex items-center justify-center transition-all duration-200 hover:scale-110 z-50"
        aria-label="Open AI Chat"
      >
        <ChatMultiple24Regular className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-[420px] h-[600px] bg-white rounded-lg shadow-4 flex flex-col z-50 border border-border-light">
      {/* Header */}
      <div className="bg-salesforce-blue text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChatMultiple24Regular className="w-5 h-5" />
          <h3 className="font-semibold">AI Assistant</h3>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <Button
              appearance="subtle"
              icon={<Delete24Regular />}
              onClick={clearMessages}
              className="text-white hover:bg-white/20"
              size="small"
            />
          )}
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={toggleChat}
            className="text-white hover:bg-white/20"
            size="small"
          />
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-1">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">👋</div>
            <h4 className="font-semibold text-dark-text mb-2">
              Welcome to AI Assistant
            </h4>
            <p className="text-sm text-gray-300 mb-4">
              Ask me anything about your real estate development model
            </p>

            {/* Predefined Questions */}
            <div className="grid grid-cols-1 gap-2">
              {PREDEFINED_QUESTIONS.map((q) => (
                <button
                  key={q.id}
                  onClick={() => handlePredefinedQuestion(q.question)}
                  className="bg-white border border-border-light rounded p-3 text-left hover:bg-gray-2 hover:border-salesforce-blue transition-all text-sm flex items-start gap-2"
                  disabled={isLoading}
                >
                  <span className="text-lg">{q.icon}</span>
                  <span className="flex-1">{q.question}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-4 py-2 ${
                message.role === 'user'
                  ? 'bg-salesforce-blue text-white'
                  : 'bg-white border border-border-light'
              }`}
            >
              <RichContentRenderer
                content={message.content}
                type={message.type}
                data={message.data}
              />
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-border-light rounded-lg px-4 py-3">
              <Spinner size="tiny" label="Thinking..." />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-border-light bg-white rounded-b-lg">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a question..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button
            appearance="primary"
            icon={<Send24Regular />}
            onClick={() => handleSendMessage()}
            disabled={!input.trim() || isLoading}
          />
        </div>
      </div>
    </div>
  );
}
